import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, TrendingDown, Tag, ArrowUpCircle, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { brl, pct, num, dm } from "@/lib/format";
import { Card, Button } from "@/components/kit";

export function WeeklyComparison() {
  const nav = useNavigate();
  const { data } = useQuery({ queryKey: ["comparison"], queryFn: () => api("/reports/comparison") });
  if (!data) return null;
  const Row = ({ r, up }) => (
    <button className="flex items-center gap-3 py-2 text-left w-full" onClick={() => nav(`/conference/${data.curr.id}`)} data-testid={`comparison-${up ? "up" : "down"}-${r.product_id}`}>
      {up ? <TrendingUp size={16} style={{ color: "var(--accent)" }} /> : <TrendingDown size={16} style={{ color: "var(--success)" }} />}
      <span className="flex-1 truncate text-sm font-semibold">{r.product_name}</span>
      <span className="caption tnum">{num(r.prev)} → {num(r.curr)} {r.unit}</span>
      <span className="label tnum w-16 text-right" style={{ color: up ? "var(--accent)" : "var(--success)" }}>
        {r.is_new ? "novo" : `${r.pct > 0 ? "+" : ""}${pct(r.pct, 0)}`}
      </span>
    </button>
  );
  return (
    <Card data-testid="weekly-comparison">
      <div className="h3">Comparativo com a conferência anterior</div>
      {!data.available ? (
        <p className="caption mt-2">Sem conferência anterior para comparar ainda.</p>
      ) : (
        <>
          <p className="caption mt-1">{dm(data.curr.week_start)} – {dm(data.curr.week_end)} vs {dm(data.prev.week_start)} – {dm(data.prev.week_end)} · consumo por produto</p>
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div>
              <div className="eyebrow mb-1">Maiores aumentos</div>
              {data.ups.length ? data.ups.map((r) => <Row key={r.product_id} r={r} up />) : <p className="caption">Nenhum produto aumentou o consumo.</p>}
            </div>
            <div>
              <div className="eyebrow mb-1">Maiores reduções</div>
              {data.downs.length ? data.downs.map((r) => <Row key={r.product_id} r={r} />) : <p className="caption">Nenhuma redução.</p>}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

export function PriceAlerts() {
  const nav = useNavigate();
  const { data = [] } = useQuery({ queryKey: ["price-alerts"], queryFn: () => api("/reports/price-alerts") });
  return (
    <Card data-testid="price-alerts-card">
      <div className="flex items-center gap-2"><Tag size={17} style={{ color: "var(--accent)" }} /><span className="h3 flex-1">Alertas de preço</span></div>
      <div className="mt-3 flex flex-col gap-2">
        {data.length === 0 && <p className="caption">Nenhum aumento acima do normal nas últimas notas.</p>}
        {data.map((a) => (
          <div key={a.product_id} className="flex items-center gap-3" data-testid={`price-alert-${a.product_id}`}>
            <ArrowUpCircle size={16} style={{ color: "var(--danger)" }} />
            <div className="flex-1 min-w-0">
              <div className="label truncate">{a.product_name}</div>
              <div className="caption">{a.supplier} · {brl(a.price)}/{a.unit} vs média {brl(a.avg)}</div>
            </div>
            <span className="badge badge-danger">+{pct(a.pct, 0)}</span>
          </div>
        ))}
      </div>
      <Button variant="secondary" size="sm" icon={Trophy} className="mt-4" onClick={() => nav("/suppliers")} data-testid="open-suppliers-button">Ver ranking de fornecedores</Button>
    </Card>
  );
}
